import {
  createRouter,
  createRootRoute,
  createRoute,
  lazyRouteComponent,
  Outlet,
} from '@tanstack/react-router'
import { createBrowserHistory } from '@tanstack/react-router'

import ProtectedLayout from './ProtectedLayout'
import AdvancedModeLayout from './AdvancedModeLayout'
import App from '@/App'
import { FEATURE_FLAGS } from '@/config/featureFlags'
import { PageSkeleton } from '@/components/ui/PageSkeleton'
import { RouteError } from './RouteError'
import IndexRoot from './IndexRoot'

const SignInPage = lazyRouteComponent(
  () => import('@/domains/auth/pages/SignInPage'),
  'SignInPage',
)
const SignUpPage = lazyRouteComponent(
  () => import('@/domains/auth/pages/SignUpPage'),
  'SignUpPage',
)
const TermsPage = lazyRouteComponent(
  () => import('@/domains/terms/pages/TermsPage'),
  'TermsPage',
)
const DashboardPage = lazyRouteComponent(
  () => import('@/domains/dashboard/pages/DashboardPage'),
  'DashboardPage',
)
const WrappedPage = lazyRouteComponent(
  () => import('@/domains/wrapped/pages/WrappedPage'),
  'WrappedPage',
)
const AccountsPage = lazyRouteComponent(
  () => import('@/domains/accounts/pages/AccountsPage'),
  'AccountsPage',
)
const TransactionsPage = lazyRouteComponent(
  () => import('@/domains/transactions/pages/TransactionsPage'),
  'TransactionsPage',
)
const CategoriesPage = lazyRouteComponent(
  () => import('@/domains/categories/pages/CategoriesPage'),
  'CategoriesPage',
)
const TagsPage = lazyRouteComponent(
  () => import('@/domains/tags/pages/TagsPage'),
  'TagsPage',
)
const SavingsPage = lazyRouteComponent(
  () => import('@/domains/savings/pages/SavingsPage'),
  'SavingsPage',
)
const SavingPocketsPage = lazyRouteComponent(
  () => import('@/domains/savings/pages/SavingPocketsPage'),
  'SavingPocketsPage',
)
const BudgetPage = lazyRouteComponent(
  () => import('@/domains/budget/pages/BudgetPage'),
  'BudgetPage',
)
const InstallmentsPage = lazyRouteComponent(
  () => import('@/domains/installments/pages/InstallmentsPage'),
  'InstallmentsPage',
)
const AnalyticsPage = lazyRouteComponent(
  () => import('@/domains/analytics/pages/AnalyticsPage'),
  'AnalyticsPage',
)
const ProfilePage = lazyRouteComponent(
  () => import('@/domains/profile/pages/ProfilePage'),
  'ProfilePage',
)
const McpOAuthConsentPage = lazyRouteComponent(
  () => import('@/domains/mcp/pages/McpOAuthConsentPage'),
  'McpOAuthConsentPage',
)
const NotFoundPage = lazyRouteComponent(
  () => import('./NotFoundPage'),
  'NotFoundPage',
)

// Root
const Root = createRootRoute({
  component: App,
})

const AuthRoute = createRoute({
  getParentRoute: () => Root,
  path: 'auth',
  component: () => <Outlet />,
})

const SignInRoute = createRoute({
  getParentRoute: () => AuthRoute,
  path: 'signin',
  component: SignInPage,
})

const SignUpRoute = createRoute({
  getParentRoute: () => AuthRoute,
  path: 'signup',
  component: SignUpPage,
})

const TermsRoute = createRoute({
  getParentRoute: () => Root,
  path: 'terms',
  component: TermsPage,
})

const OAuthRoute = createRoute({
  getParentRoute: () => Root,
  path: 'oauth',
  component: () => <Outlet />,
})

const McpOAuthConsentRoute = createRoute({
  getParentRoute: () => OAuthRoute,
  path: 'consent',
  component: McpOAuthConsentPage,
})

const ProtectedRoute = createRoute({
  getParentRoute: () => Root,
  id: '_protected',
  component: ProtectedLayout,
})

const AdvancedModeRoute = createRoute({
  getParentRoute: () => ProtectedRoute,
  id: '_advanced-mode',
  component: AdvancedModeLayout,
})

const DashboardRoute = createRoute({
  getParentRoute: () => ProtectedRoute,
  path: 'dashboard',
  component: DashboardPage,
})

const WrappedRoute = createRoute({
  getParentRoute: () => ProtectedRoute,
  path: 'wrapped',
  component: WrappedPage,
})

const WrappedPeriodRoute = createRoute({
  getParentRoute: () => ProtectedRoute,
  path: 'wrapped/$period',
  component: WrappedPage,
})

const AccountsRoute = createRoute({
  getParentRoute: () => ProtectedRoute,
  path: 'accounts',
  component: AccountsPage,
})

const TransactionsRoute = createRoute({
  getParentRoute: () => ProtectedRoute,
  path: 'transactions',
  component: TransactionsPage,
})

const CategoriesRoute = createRoute({
  getParentRoute: () => ProtectedRoute,
  path: 'categories',
  component: CategoriesPage,
})

const TagsRoute = createRoute({
  getParentRoute: () => ProtectedRoute,
  path: 'tags',
  component: TagsPage,
})

const SavingsRoute = createRoute({
  getParentRoute: () => AdvancedModeRoute,
  path: 'savings',
  component: SavingsPage,
})

const SavingPocketsRoute = createRoute({
  getParentRoute: () => AdvancedModeRoute,
  path: 'saving-pockets',
  component: SavingPocketsPage,
})

const BudgetRoute = createRoute({
  getParentRoute: () => AdvancedModeRoute,
  path: 'budget',
  component: BudgetPage,
})

const InstallmentsRoute = createRoute({
  getParentRoute: () => AdvancedModeRoute,
  path: 'installments',
  component: InstallmentsPage,
})

const AnalyticsRoute = createRoute({
  getParentRoute: () => AdvancedModeRoute,
  path: 'analytics',
  component: AnalyticsPage,
})

const ProfileRoute = createRoute({
  getParentRoute: () => ProtectedRoute,
  path: 'profile',
  component: ProfilePage,
})

const IndexRoute = createRoute({
  getParentRoute: () => Root,
  path: '/',
  component: IndexRoot,
})

const NotFoundRoute = createRoute({
  getParentRoute: () => Root,
  path: '*',
  component: NotFoundPage,
})

const appRouter = createRouter({
  routeTree: Root.addChildren([
    IndexRoute,
    AuthRoute.addChildren([SignInRoute, SignUpRoute]),
    OAuthRoute.addChildren([McpOAuthConsentRoute]),
    TermsRoute,
    ProtectedRoute.addChildren([
      DashboardRoute,
      ...(FEATURE_FLAGS.wrapped ? [WrappedRoute, WrappedPeriodRoute] : []),
      AccountsRoute,
      TransactionsRoute,
      CategoriesRoute,
      TagsRoute,
      ProfileRoute,
      AdvancedModeRoute.addChildren([
        BudgetRoute,
        InstallmentsRoute,
        SavingsRoute,
        AnalyticsRoute,
        ...(FEATURE_FLAGS.savingPockets ? [SavingPocketsRoute] : []),
      ]),
    ]),
    NotFoundRoute,
  ]),
  history: createBrowserHistory(),
  defaultPendingComponent: PageSkeleton,
  defaultErrorComponent: RouteError,
})

export default appRouter
